/**
 * LeadContactSections.jsx — the full Contact Info tab for the Lead Focus page.
 *
 * Renders 80+ fields from Template1_ExistingLeads + Template2_NewFreshLeads
 * across 8 collapsible sections. Every visible value is click-to-edit: a
 * single tap on the value activates the right input type (text, number, date,
 * textarea, single-select, or multi-select pills). All changes flow through
 * onFieldUpdate(key, value) so the parent can persist.
 *
 * CSV fields marked "existing leads only" render as read-only (gray). The
 * data model for multi-select fields stores arrays in memory and serialises
 * to semicolon-separated strings for CSV parity (handled by helpers).
 */
import React, { createContext, useContext, useMemo, useState, useEffect } from "react";
import axios from "axios";
import { ChevronDown, ChevronRight, Sparkles, X, Edit3, Plus, Check } from "lucide-react";
import UserSelector from "@/components/UserSelector";
import { ContactListEditor } from "./DialerDemoExtensions";
import { cleanScalar } from "@/lib/leadDisplay";
import { LeadPracticeAddressBlock } from "@/components/LeadAddressBlock";
import { API, useAuth } from "@/App";
import { OPT, useFieldOptions } from "@/lib/leadFieldOptions";

// Module-level cache so we fetch the user directory only once per session.
let __directoryCache = null;
let __directoryPromise = null;
async function loadDirectory(token) {
  if (__directoryCache) return __directoryCache;
  if (__directoryPromise) return __directoryPromise;
  __directoryPromise = axios
    .get(`${API}/users/directory`, { headers: { Authorization: `Bearer ${token}` } })
    .then((res) => {
      const rows = Array.isArray(res.data?.users) ? res.data.users : [];
      __directoryCache = rows.map((u) => ({
        id: u.id,
        name: u.full_name || u.email || "Unnamed",
        role: (u.role || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        email: u.email,
      }));
      return __directoryCache;
    })
    .catch(() => {
      __directoryCache = [];
      return [];
    })
    .finally(() => { __directoryPromise = null; });
  return __directoryPromise;
}

// Color map for badge-style single-selects (lead rating, sentiment, etc.)
const BADGE_COLORS = {
  Hot: { bg: "#FCEBEB", fg: "#A32D2D" },
  Warm: { bg: "#FAEEDA", fg: "#854F0B" },
  Cold: { bg: "#E6F1FB", fg: "#185FA5" },
  Dormant: { bg: "#f5f5f5", fg: "#666" },
  "Very Interested": { bg: "#DAFBE8", fg: "#0F6E56" },
  "Moderately Interested": { bg: "#D4F4E6", fg: "#0F6E56" },
  "Low Interest": { bg: "#FAEEDA", fg: "#854F0B" },
  "Not Interested": { bg: "#FCEBEB", fg: "#A32D2D" },
  "Highly Engaged": { bg: "#DAFBE8", fg: "#0F6E56" },
  "No Engagement": { bg: "#FCEBEB", fg: "#A32D2D" },
  Positive: { bg: "#DAFBE8", fg: "#0F6E56" },
  Negative: { bg: "#FCEBEB", fg: "#A32D2D" },
  Neutral: { bg: "#f5f5f5", fg: "#666" },
  Mixed: { bg: "#FAEEDA", fg: "#854F0B" },
  "Within Budget": { bg: "#DAFBE8", fg: "#0F6E56" },
  "Above Budget": { bg: "#FCEBEB", fg: "#A32D2D" },
  Negotiable: { bg: "#FAEEDA", fg: "#854F0B" },
};

const TAG_COLORS = {
  purple: { bg: "#EEEDFE", fg: "#3C3489" },
  blue:   { bg: "#E6F1FB", fg: "#185FA5" },
  teal:   { bg: "#D4F4E6", fg: "#0F6E56" },
  green:  { bg: "#DAFBE8", fg: "#0F6E56" },
  amber:  { bg: "#FAEEDA", fg: "#854F0B" },
  red:    { bg: "#FCEBEB", fg: "#A32D2D" },
  gray:   { bg: "#f5f5f5", fg: "#666" },
};

// ── Reusable editable field ────────────────────────────────────────
// When InstantSaveCtx is true, fields save on pick / blur with no Save button.
export const InstantSaveCtx = createContext(false);

export function EditableField({
  label, value, type = "text", options = [], onSave, aiFilled, readOnly, pillColor = "gray",
  badge, placeholder, highlight,
}) {
  const instant = useContext(InstantSaveCtx);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  React.useEffect(() => { setDraft(value ?? ""); }, [value]);

  const isMulti = type === "multiselect";
  const currentArr = isMulti ? (Array.isArray(value) ? value : (value ? String(value).split(";").map(s => s.trim()).filter(Boolean) : [])) : null;
  const draftArr = isMulti ? (Array.isArray(draft) ? draft : (draft ? String(draft).split(";").map(s => s.trim()).filter(Boolean) : [])) : null;

  const commit = (v) => { onSave?.(v); setEditing(false); };
  const cancel = () => { setDraft(value ?? ""); setEditing(false); };

  const fieldBorder = highlight ? "1.5px solid #2563eb" : editing ? "1.5px solid #2563eb" : "0.5px solid transparent";
  const labelStyle = { fontSize: 10, textTransform: "uppercase", color: "#888", letterSpacing: "0.3px", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 6 };
  const wrap = { padding: "8px 10px", borderRadius: 6, border: fieldBorder, background: editing ? "#f9faff" : "transparent", cursor: readOnly ? "default" : "pointer", transition: "background 0.15s" };

  // ─── Display mode ───
  if (!editing) {
    const hasValue = isMulti ? (currentArr && currentArr.length > 0) : (value !== null && value !== undefined && value !== "");
    return (
      <div
        data-testid={`field-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        data-highlight={highlight ? "true" : "false"}
        onClick={() => !readOnly && setEditing(true)}
        onMouseOver={(e) => !readOnly && (e.currentTarget.style.background = "#f9faff")}
        onMouseOut={(e) => !readOnly && (e.currentTarget.style.background = editing ? "#f9faff" : "transparent")}
        style={wrap}
      >
        <div style={labelStyle}>
          {label}
          {aiFilled && <span title="AI-inferred" style={{ background: "#FAEEDA", color: "#854F0B", padding: "1px 6px", borderRadius: 10, fontSize: 9, fontWeight: 600 }}>✦ AI · Inferred</span>}
          {badge && <span style={{ background: "#E6F1FB", color: "#185FA5", padding: "1px 6px", borderRadius: 10, fontSize: 9, fontWeight: 600 }}>{badge}</span>}
        </div>
        <div style={{ marginTop: 3, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", minHeight: 18 }}>
          {!hasValue && !readOnly && (
            <span style={{ fontSize: 12, color: "#aaa", display: "inline-flex", alignItems: "center", gap: 4 }}>
              Not set <Edit3 style={{ width: 9, height: 9 }} />
            </span>
          )}
          {!hasValue && readOnly && (
            <span style={{ fontSize: 12, color: "#bbb" }}>—</span>
          )}
          {isMulti && hasValue && currentArr.map((v, i) => {
            const c = TAG_COLORS[pillColor] || TAG_COLORS.gray;
            return <span key={i} style={{ background: c.bg, color: c.fg, borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 500 }}>{v}</span>;
          })}
          {!isMulti && hasValue && type === "badge" && (() => {
            const bc = BADGE_COLORS[value] || TAG_COLORS[pillColor] || TAG_COLORS.gray;
            return <span style={{ background: bc.bg, color: bc.fg, borderRadius: 10, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>{value}</span>;
          })()}
          {!isMulti && hasValue && type !== "badge" && type !== "textarea" && (
            <span style={{ fontSize: 13, fontWeight: 500, color: "#222" }}>{String(value)}</span>
          )}
          {!isMulti && hasValue && type === "textarea" && (
            <span style={{ fontSize: 12, color: "#444", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{String(value)}</span>
          )}
        </div>
      </div>
    );
  }

  // ─── Edit mode ───
  const inputStyle = { width: "100%", border: "0.5px solid #bbb", borderRadius: 6, padding: "6px 8px", fontSize: 13, fontFamily: "inherit" };
  const slug = (label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const blurSave = instant ? { onBlur: () => (String(draft) === String(value ?? "") ? setEditing(false) : commit(draft)) } : {};
  const pick = (v) => { if (!instant) { setDraft(v); return; } if (v === (value ?? "")) setEditing(false); else commit(v); };
  const multiChange = (v) => { setDraft(v); if (instant) onSave?.(v); };
  return (
    <div data-testid={`field-edit-${slug}`} style={wrap}>
      <div style={labelStyle}>{label}</div>
      <div style={{ marginTop: 4 }}>
        {type === "text" && <input autoFocus value={draft} onChange={e => setDraft(e.target.value)} placeholder={placeholder} style={inputStyle} {...blurSave} />}
        {type === "number" && <input autoFocus type="number" value={draft} onChange={e => setDraft(e.target.value)} placeholder={placeholder} style={inputStyle} {...blurSave} />}
        {type === "date" && <input autoFocus type="date" value={draft} onChange={e => setDraft(e.target.value)} style={inputStyle} {...blurSave} />}
        {type === "datetime" && <input autoFocus type="datetime-local" value={draft} onChange={e => setDraft(e.target.value)} style={inputStyle} {...blurSave} />}
        {type === "textarea" && <textarea autoFocus value={draft} onChange={e => setDraft(e.target.value)} rows={4} placeholder={placeholder} style={{ ...inputStyle, resize: "vertical", minHeight: 80 }} {...blurSave} />}
        {(type === "select" || type === "badge") && (
          <select autoFocus value={draft} onChange={e => pick(e.target.value)} onBlur={instant ? () => setEditing(false) : undefined} style={inputStyle} data-testid={`field-select-${slug}`}>
            <option value="">— Select —</option>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
        {isMulti && (
          <MultiSelectEditor options={options} value={draftArr || []} onChange={multiChange} pillColor={pillColor} />
        )}
      </div>
      {instant ? (
        isMulti && (
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <button data-testid={`field-done-${slug}`} onClick={() => setEditing(false)} className="btn" style={{ fontSize: 11, padding: "5px 12px" }}>
              <Check style={{ width: 10, height: 10 }} /> Done
            </button>
          </div>
        )
      ) : (
      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <button data-testid={`field-save-${slug}`} onClick={() => commit(isMulti ? (draftArr || []) : draft)} className="btn btn-primary" style={{ fontSize: 11, padding: "5px 12px" }}>
          <Check style={{ width: 10, height: 10 }} /> Save
        </button>
        <button onClick={cancel} className="btn" style={{ fontSize: 11, padding: "5px 12px" }}>Cancel</button>
      </div>
      )}
    </div>
  );
}

function MultiSelectEditor({ options, value, onChange, pillColor }) {
  const [filter, setFilter] = useState("");
  const remove = (v) => onChange(value.filter(x => x !== v));
  const add = (v) => { if (!value.includes(v)) onChange([...value, v]); };
  const filtered = options.filter(o => !value.includes(o) && (filter === "" || o.toLowerCase().includes(filter.toLowerCase())));
  const c = TAG_COLORS[pillColor] || TAG_COLORS.gray;
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, minHeight: 28, padding: "4px 6px", border: "0.5px solid #ddd", borderRadius: 6, background: "#fff" }}>
        {value.map((v, i) => (
          <span key={i} style={{ background: c.bg, color: c.fg, borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 4 }}>
            {v}
            <button onClick={() => remove(v)} style={{ background: "none", border: "none", color: c.fg, cursor: "pointer", padding: 0, display: "inline-flex" }}>
              <X style={{ width: 9, height: 9 }} />
            </button>
          </span>
        ))}
        <input
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={value.length === 0 ? "Search or pick options…" : "Add more…"}
          style={{ border: "none", outline: "none", fontSize: 12, flex: 1, minWidth: 100, background: "transparent", fontFamily: "inherit" }}
        />
      </div>
      {filtered.length > 0 && (
        <div style={{ maxHeight: 160, overflowY: "auto", border: "0.5px solid #ddd", borderRadius: 6, marginTop: 4, background: "#fff" }}>
          {filtered.map(o => (
            <button key={o} onClick={() => { add(o); setFilter(""); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", border: "none", background: "transparent", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}
              onMouseOver={e => e.currentTarget.style.background = "#f5f5f5"} onMouseOut={e => e.currentTarget.style.background = "transparent"}>
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Collapsible section wrapper ────────────────────────────────────
// Compact (Sales Dialer Quick edit) mode starts every section collapsed.
const StartCollapsedCtx = createContext(false);

export function Section({ id, title, children, defaultOpen = false, count, highlighted, hasContent = false, forceOpen }) {
  const startCollapsed = useContext(StartCollapsedCtx);
  // Iter 140.2 (stephanie@ehiehr.com issue 59ab7ec8): when the section
  // has user-entered content, open it by default on mount so reps can
  // immediately see their saved data instead of having to click to
  // expand and re-verify their work. Previously the "Next Actions &
  // Ownership" section stayed collapsed on refresh, making saved
  // notes look like they had disappeared.
  const [open, setOpen] = useState(!startCollapsed && (defaultOpen || hasContent));
  // A changing truthy `forceOpen` value (e.g. a click counter) expands the section.
  useEffect(() => { if (forceOpen) setOpen(true); }, [forceOpen]);
  return (
    <div data-testid={`section-${id}`} style={{ background: "#fff", border: "0.5px solid #eeeeee", borderRadius: 10, marginBottom: 10, boxShadow: highlighted ? "0 0 0 2px #2563eb33" : "none" }}>
      <button
        data-testid={`section-toggle-${id}`}
        onClick={() => setOpen(!open)}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", background: "#f9f9f9", borderRadius: open ? "10px 10px 0 0" : 10, padding: "12px 16px", border: "none", cursor: "pointer", fontFamily: "inherit" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{title}</span>
          {count != null && (
            <span style={{ background: "#eee", color: "#666", padding: "1px 7px", borderRadius: 10, fontSize: 10, fontWeight: 600 }}>{count} fields</span>
          )}
        </div>
        {open ? <ChevronDown style={{ width: 14, height: 14, color: "#888" }} /> : <ChevronRight style={{ width: 14, height: 14, color: "#888" }} />}
      </button>
      {open && <div style={{ padding: 14 }}>{children}</div>}
    </div>
  );
}

// ── User selector field (wraps UserSelector in the Section UX) ─────
export function UserField({ label, value, onSave, multi = false, testid, readOnly = false }) {
  const { token } = useAuth();
  const instant = useContext(InstantSaveCtx);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [directory, setDirectory] = useState(__directoryCache || []);
  React.useEffect(() => { setDraft(value || ""); }, [value]);
  // Lazy-load the real user directory the first time anyone edits a
  // user field. Cached at module scope so subsequent edits are instant.
  useEffect(() => {
    if (!editing || !token || __directoryCache) return;
    let cancelled = false;
    loadDirectory(token).then((rows) => { if (!cancelled) setDirectory(rows); });
    return () => { cancelled = true; };
  }, [editing, token]);
  const current = value ? String(value).split(";").map(s => s.trim()).filter(Boolean) : [];
  const labelStyle = { fontSize: 10, textTransform: "uppercase", color: "#888", letterSpacing: "0.3px", fontWeight: 500 };
  const wrap = { padding: "8px 10px", borderRadius: 6, cursor: editing ? "default" : "pointer" };
  if (!editing) {
    return (
      <div
        data-testid={`field-${testid || (label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        onClick={() => !readOnly && setEditing(true)}
        onMouseOver={(e) => { if (!readOnly) e.currentTarget.style.background = "#f9faff"; }}
        onMouseOut={(e) => { e.currentTarget.style.background = "transparent"; }}
        style={{ ...wrap, cursor: readOnly ? "default" : "pointer" }}
      >
        <div style={labelStyle}>{label}</div>
        <div style={{ marginTop: 3, display: "flex", flexWrap: "wrap", gap: 4, minHeight: 18 }}>
          {current.length === 0
            ? (readOnly ? <span style={{ fontSize: 12, color: "#bbb" }}>—</span> : <span style={{ fontSize: 12, color: "#aaa", display: "inline-flex", alignItems: "center", gap: 4 }}>Not assigned <Edit3 style={{ width: 9, height: 9 }} /></span>)
            : current.map((n, i) => (
                <span key={i} style={{ background: "#E6F1FB", color: "#185FA5", borderRadius: 20, padding: "2px 8px", fontSize: 11, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 14, height: 14, background: "#185FA5", color: "#fff", borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 700 }}>
                    {n.split(" ").slice(0, 2).map(s => (s || "")[0] || "").join("").toUpperCase()}
                  </span>
                  {n}
                </span>
              ))
          }
        </div>
      </div>
    );
  }
  return (
    <div data-testid={`field-edit-${testid || (label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} style={{ ...wrap, background: "#f9faff", border: "1.5px solid #2563eb" }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ marginTop: 4 }}>
        <UserSelector value={draft} onChange={(v) => { setDraft(v); if (instant) { onSave?.(v); if (!multi) setEditing(false); } }} multi={multi} users={directory} testid={`user-sel-${testid || "user"}`} />
      </div>
      {instant ? (
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <button data-testid={`user-done-${testid || "user"}`} onClick={() => setEditing(false)} className="btn" style={{ fontSize: 11, padding: "5px 12px" }}>
            <Check style={{ width: 10, height: 10 }} /> Done
          </button>
        </div>
      ) : (
      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <button onClick={() => { onSave?.(draft); setEditing(false); }} className="btn btn-primary" style={{ fontSize: 11, padding: "5px 12px" }}>
          <Check style={{ width: 10, height: 10 }} /> Save
        </button>
        <button onClick={() => { setDraft(value || ""); setEditing(false); }} className="btn" style={{ fontSize: 11, padding: "5px 12px" }}>Cancel</button>
      </div>
      )}
    </div>
  );
}

// ── Notes history (append-only, timestamped) ──────────────────────
// Each saved entry is prepended to a newline-separated log of
// "[YYYY-MM-DD HH:mm — author] note" lines. The text is rendered as a
// scrollable list of cards.
//
// Iter 217 (Aug 2026 · Bug — Ioannis report: "In the Next Actions &
// Ownership → Lead Notes section next to the date and timestamp the
// person that added that lead note name should be there. It always
// just says 'You'."). Root cause: the parent hardcoded `authorName`
// to the literal string "You" (or "Super Admin"), so every note ever
// persisted through this section landed in `lead.lead_notes` as
// `[<ts> — You] <body>` — losing the author's identity at write time.
// The `authorName` prop must now be a resolved display name for the
// currently-logged-in rep (their full_name / name / email — NEVER
// the literal "You"). The default fallback is "Rep" so a caller who
// forgets the prop still can't leak "You" into the persisted log.
export function NotesHistoryField({ label, value, onSave, authorName = "Rep", readOnly = false }) {
  // ── Iter 138 (stephanie@ehiehr.com / ioannis@ehiehr.com bug fix —
  //    issues 42439961, 2882d638): the parent used to pass
  //    `value={lead.lead_notes || lead.notes}`. When lead_notes was
  //    empty but lead.notes was the STRUCTURED ARRAY from the right-rail
  //    notes widget, JS coerced the array to "[object Object],[object
  //    Object]" when concatenated below in submit() — and that garbage
  //    got saved as the new lead_notes. On the next refresh reps saw
  //    "object Object unknown" instead of their note. The defensive
  //    guard below filters out anything that isn't already a string so
  //    we can't pollute the field.
  const safeValue = (typeof value === "string") ? value : "";
  const [draft, setDraft] = useState("");
  const entries = useMemo(() => parseNotesLog(safeValue), [safeValue]);
  const submit = () => {
    const trimmed = (draft || "").trim();
    if (!trimmed) return;
    // Iter 248 (Jun 2026) — save a full ISO-8601 UTC timestamp (with
    // seconds + Z) so it converts cleanly to local display time. Older
    // notes were saved as a shorter space-separated UTC string
    // ("YYYY-MM-DD HH:mm"); formatNoteTs() handles both at display time.
    const ts = new Date().toISOString();
    // Iter 217 — belt-and-suspenders. Even if a future caller
    // regresses and passes "You" / "you" / empty / whitespace to this
    // component, we normalize to a safe default so the persisted log
    // never contains "— You]". The correct fix is at the call site
    // (pass a real display name from the logged-in user); this guard
    // exists strictly to prevent a repeat of the Ioannis-report bug.
    const rawAuthor = (typeof authorName === "string" ? authorName : "").trim();
    const isYouSentinel = /^you$/i.test(rawAuthor);
    const safeAuthor = (!rawAuthor || isYouSentinel) ? "Rep" : rawAuthor;
    const next = `[${ts} — ${safeAuthor}] ${trimmed}\n` + safeValue;
    onSave?.(next);
    setDraft("");
  };
  return (
    <div data-testid="field-lead-notes" style={{ padding: "8px 10px", borderRadius: 6 }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", color: "#888", letterSpacing: "0.3px", fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}>
        {label}
        <span style={{ background: "#f5f5f5", color: "#666", padding: "1px 6px", borderRadius: 10, fontSize: 9, fontWeight: 600 }}>{entries.length} entr{entries.length === 1 ? "y" : "ies"}</span>
      </div>
      {!readOnly && <div style={{ marginTop: 6 }}>
        <textarea
          data-testid="lead-notes-input"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="Add a note — it will be timestamped and attributed on save."
          rows={3}
          style={{ width: "100%", border: "0.5px solid #ddd", borderRadius: 6, padding: "7px 9px", fontSize: 12, fontFamily: "inherit", resize: "vertical", minHeight: 60 }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
          <button data-testid="lead-notes-add" onClick={submit} disabled={!(draft || "").trim()} className="btn btn-primary" style={{ fontSize: 11, padding: "5px 14px", opacity: (draft || "").trim() ? 1 : 0.4 }}>
            <Plus style={{ width: 10, height: 10 }} /> Add note
          </button>
        </div>
      </div>}
      <div data-testid="lead-notes-history" style={{ marginTop: 10, maxHeight: 220, overflowY: "auto", border: "0.5px solid #eee", borderRadius: 6, padding: 4 }}>
        {entries.length === 0
          ? <div style={{ fontSize: 11, color: "#aaa", padding: "10px 8px" }}>No notes yet.</div>
          : entries.map((e, i) => (
              <div key={i} data-testid={`lead-note-entry-${i}`} style={{ padding: "8px 10px", borderBottom: i < entries.length - 1 ? "0.5px solid #f2f2f2" : "none" }}>
                <div style={{ fontSize: 10, color: "#888", marginBottom: 3, display: "flex", gap: 8 }}>
                  <span data-testid={`lead-note-ts-${i}`}>{formatNoteTs(e.ts)}</span>
                  <span style={{ color: "#185FA5", fontWeight: 600 }}>{e.author}</span>
                </div>
                <div style={{ fontSize: 12, color: "#222", whiteSpace: "pre-wrap", lineHeight: 1.45 }}>{e.text}</div>
              </div>
            ))
        }
      </div>
    </div>
  );
}
// Iter 248 (Jun 2026) — render a note's stored UTC timestamp in Eastern
// time, 12-hour AM/PM, with a DST-aware zone label. Handles both the new
// full ISO-8601 format ("2026-09-11T18:45:30.123Z") and the legacy
// space-separated UTC string ("2026-09-11 18:45"). Degrades gracefully:
// empty/blank → "", unparseable → the raw string (never throws).
function formatNoteTs(tsRaw) {
  if (!tsRaw || typeof tsRaw !== "string") return "";
  const s = tsRaw.trim();
  if (!s) return "";
  let d;
  if (/(Z|[+-]\d{2}:?\d{2})$/.test(s)) {
    // Already carries a timezone/offset.
    d = new Date(s);
  } else {
    // Legacy UTC string with no zone marker — normalize to ISO-UTC.
    let iso = s.replace(" ", "T");
    if (/T\d{2}:\d{2}$/.test(iso)) iso += ":00";
    d = new Date(iso + "Z");
  }
  if (!d || isNaN(d.getTime())) return tsRaw;
  try {
    const datePart = d.toLocaleString("en-US", {
      timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric",
    });
    const timePart = d.toLocaleString("en-US", {
      timeZone: "America/New_York", hour: "numeric", minute: "2-digit",
      hour12: true, timeZoneName: "short",
    });
    return `${datePart} · ${timePart}`;
  } catch {
    return tsRaw;
  }
}
function parseNotesLog(v) {
  // Defensive guard — only strings are valid notes-log payloads. Arrays
  // and objects historically slipped through here when the parent
  // fallback hit the structured right-rail notes array, producing the
  // infamous "[object Object] unknown" entries reps complained about.
  if (!v || typeof v !== "string") return [];
  const lines = v.split("\n").filter(l => l.trim() !== "");
  const out = [];
  for (const line of lines) {
    const m = line.match(/^\[([^\]]+)\]\s*(.*)$/);
    if (m) {
      const [ts, author] = (m[1] || "").split("—").map(s => s.trim());
      out.push({ ts, author: author || "unknown", text: m[2] });
    } else {
      out.push({ ts: "", author: "", text: line });
    }
  }
  return out;
}

// ── Main: 8 collapsible sections ───────────────────────────────────
// The parent owns the lead object + an onFieldUpdate(key, value) callback.
// All field keys map to lead fields. A helper normalises comma-separated
// strings and arrays so the CSV importer can round-trip.
export default function LeadContactSections({ lead, onFieldUpdate, userRole = "sales_manager", currentUser = null, focusedField, missionCriticalKeys = [], highlightMissing = false, compact = false }) {
  useFieldOptions();
  if (!lead) return null;
  const isSuper = userRole === "super_admin";
  // Iter 217 — resolve the ACTUAL display name for the currently-
  // logged-in rep so every note we write into `lead.lead_notes`
  // carries their real identity (not the literal string "You"). Order
  // of preference: full_name → name → email → role-based fallback.
  // Every call site of this component now threads `currentUser` from
  // its `useAuth()` context; if a caller ever forgets, we still avoid
  // the "You" sentinel via a role-derived label.
  const _rawFullName = (currentUser?.full_name || "").trim();
  const _rawName = (currentUser?.name || "").trim();
  const _rawEmail = (currentUser?.email || "").trim();
  const resolvedAuthorName =
    _rawFullName
    || _rawName
    || _rawEmail
    || (userRole === "super_admin" ? "Super Admin" : "Rep");
  const save = (k, v) => onFieldUpdate?.(k, v);
  const grid2 = { display: "grid", gridTemplateColumns: compact ? "1fr" : "1fr 1fr", gap: 10 };
  const isSoftwareAdvice = (lead.lead_source || "") === "Software Advice";
  const isLost = ["Closed Lost", "Implementation Lost"].includes(lead.lead_stage || lead.stage);

  // Helper to pass focused-field highlight. Now also pulses required-but-empty
  // mission-critical fields when the "Show missing required" toggle is on.
  const isEmpty = (k) => {
    const v = lead[k] ?? lead[`${k}_multi`];
    if (v == null) return true;
    if (Array.isArray(v)) return v.length === 0;
    if (typeof v === "string") return v.trim().length === 0;
    return false;
  };
  const hl = (k) => focusedField === k || (highlightMissing && missionCriticalKeys.includes(k) && isEmpty(k));

  return (
    <StartCollapsedCtx.Provider value={compact}>
    <div data-testid="contact-sections">
      {/* Section 1 — Contact & Practice Identity (expanded) */}
      <Section id="identity" title="Contact & Practice Identity" defaultOpen count={12}>
        <div style={grid2}>
          <EditableField label="Contact Name" value={lead.contact_name} type="text" onSave={v => save("contact_name", v)} highlight={hl("contact_name")} />
          <EditableField label="Phone" value={lead.phone} type="text" placeholder="(XXX) XXX-XXXX" onSave={v => save("phone", v)} highlight={hl("phone")} />
          <EditableField label="Email" value={lead.email} type="text" placeholder="name@example.com" onSave={v => save("email", v)} highlight={hl("email")} />
          <EditableField label="Company Name" value={cleanScalar(lead.company) || cleanScalar(lead.title)} type="text" onSave={v => save("company", v)} />
          <EditableField label="Organizational Role" value={lead.organizational_role} type="multiselect" options={OPT.organizationalRole} pillColor="purple" onSave={v => save("organizational_role", v)} highlight={hl("organizational_role")} />
          <EditableField label="Practice Type" value={lead.practice_type} type="select" options={OPT.practiceType} onSave={v => save("practice_type", v)} highlight={hl("practice_type")} />
          <EditableField label="Specialty" value={lead.specialty_multi || lead.specialty} type="multiselect" options={OPT.specialty} pillColor="blue" onSave={v => save("specialty_multi", v)} highlight={hl("specialty")} />
          <EditableField label="Time Zone" value={lead.time_zone || lead.timezone} type="select" options={OPT.timeZone} onSave={v => save("time_zone", v)} aiFilled={!!lead.time_zone_ai} highlight={hl("time_zone")} />
          <LeadPracticeAddressBlock lead={lead} onSave={(addresses) => save("addresses", addresses)} />
          <EditableField label="Number of Providers" value={lead.providers_multi} type="multiselect" options={OPT.providers} pillColor="gray" onSave={v => save("providers_multi", v)} highlight={hl("providers")} />
          <EditableField label="Practice Characteristics" value={lead.practice_characteristics} type="select" options={OPT.practiceCharacteristics} onSave={v => save("practice_characteristics", v)} />
          <div style={{ gridColumn: "1 / -1" }}>
            <EditableField label="Description" value={lead.description} type="textarea" placeholder="General practice description or notes" onSave={v => save("description", v)} />
          </div>
          {isSoftwareAdvice && (
            <EditableField label="Software Advice Rep" value={lead.software_advice_rep} type="text" onSave={v => save("software_advice_rep", v)} />
          )}
        </div>
      </Section>

      {/* Section 1b — Additional Contacts (multi-contact editor) */}
      <Section id="additional-contacts" title="Additional Contacts" defaultOpen count={(lead.contacts || []).length}>
        <div style={{ padding: "4px 0" }}>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 10, lineHeight: 1.5 }}>
            Capture every person involved at this practice — primary contact, decision makers, office admins, billing staff — each with their own typed phones (cell / office / fax / direct / work) and emails (work / billing / personal / assistant) plus role or title.
          </div>
          <ContactListEditor
            contacts={lead.contacts || []}
            onChange={(contacts) => save("contacts", contacts)}
          />
          {(lead.contacts || []).length === 0 && (
            <button
              data-testid="add-first-contact-btn"
              onClick={() => {
                // Seed the first contact with lead-level identity so
                // the card doesn't render blank for fields that already
                // exist on the parent lead. Only seeds — never
                // overwrites — because contacts.length === 0 here.
                const seedSpecialty = (Array.isArray(lead.specialty_multi) && lead.specialty_multi[0])
                  ? lead.specialty_multi[0]
                  : (lead.specialty || "");
                save("contacts", [{
                  id: `c-${Math.random().toString(36).slice(2, 8)}`,
                  name: lead.contact_name || "",
                  title: lead.organizational_role || "",
                  specialty: seedSpecialty,
                  timezone: lead.timezone || "",
                  phones: lead.phone ? [{ id: "p-1", label: "primary", number: lead.phone }] : [{ id: "p-1", label: "primary", number: "" }],
                  emails: lead.email ? [{ id: "e-1", label: "work", address: lead.email }] : [{ id: "e-1", label: "work", address: "" }],
                }]);
              }}
              className="btn btn-primary"
              style={{ fontSize: 11, marginTop: 8 }}
            >
              + Add first contact
            </button>
          )}
        </div>
      </Section>

      {/* Section 2 — Pipeline & Lead Status (expanded) */}
      <Section id="pipeline" title="Pipeline & Lead Status" defaultOpen count={11}>
        <div style={grid2}>
          <EditableField label="Pipeline" value={lead.pipeline || "Sales Pipeline Prospect"} type="text" readOnly />
          <EditableField label="Lead Stage" value={lead.lead_stage || lead.stage} type="select" options={OPT.leadStage} onSave={v => save("lead_stage", v)} aiFilled={!!lead.lead_stage_ai} highlight={hl("lead_stage")} />
          <EditableField label="Lead Source" value={lead.lead_source} type="select" options={OPT.leadSource} onSave={v => save("lead_source", v)} />
          <EditableField label="Lead Rating" value={lead.lead_rating} type="badge" options={OPT.leadRating} onSave={v => save("lead_rating", v)} aiFilled={!!lead.lead_rating_ai} highlight={hl("lead_rating")} />
          <EditableField label="Lead Status" value={lead.lead_status} type="select" options={OPT.leadStatus} onSave={v => save("lead_status", v)} />
          <EditableField label="Lead Tag" value={lead.lead_tag} type="multiselect" options={OPT.leadTag} pillColor="gray" onSave={v => save("lead_tag", v)} />
          <UserField label="Assigned" value={lead.assigned_users} onSave={v => save("assigned_users", v)} multi testid="assigned" />
          <UserField label="Followers" value={lead.followers} onSave={v => save("followers", v)} multi testid="followers" />
          <EditableField label="Lead Stage Stands" value={lead.lead_stage_stands} type="select" options={OPT.stageStands} readOnly badge="auto" onSave={v => save("lead_stage_stands", v)} />
          <EditableField label="Last Call Communication Status" value={lead.last_call_status} type="select" options={OPT.lastCallStatus} readOnly badge="auto" onSave={v => save("last_call_status", v)} />
          <div style={{ gridColumn: "1 / -1" }}>
            <EditableField label="Key Decision Makers Identified" value={lead.key_decision_makers} type="textarea" aiFilled={lead.key_decision_makers_ai} onSave={v => save("key_decision_makers", v)} highlight={hl("key_decision_makers")} />
          </div>
        </div>
      </Section>

      {/* Section 3 — Products & Qualification (expanded) */}
      <Section id="products" title="Products & Qualification" defaultOpen count={11}>
        <div style={grid2}>
          <EditableField label="Product Type" value={lead.product_type || lead.products_of_interest} type="multiselect" options={OPT.productType} pillColor="purple" onSave={v => save("product_type", v)} highlight={hl("product_type")} />
          <EditableField label="Feature Interest Category" value={lead.feature_interest} type="multiselect" options={OPT.featureInterest} pillColor="blue" onSave={v => save("feature_interest", v)} />
          <EditableField label="Product Value Drivers" value={lead.product_value_drivers} type="multiselect" options={OPT.productValue} pillColor="teal" onSave={v => save("product_value_drivers", v)} />
          <EditableField label="Key Value Proposition" value={lead.key_value_proposition} type="multiselect" options={OPT.keyValueProp} pillColor="green" onSave={v => save("key_value_proposition", v)} />
          <EditableField label="Integration Requirements" value={lead.integration_requirements} type="multiselect" options={OPT.integrationReq} pillColor="gray" onSave={v => save("integration_requirements", v)} />
          <EditableField label="Migration Needs Group" value={lead.migration_needs} type="select" options={OPT.migrationNeeds} onSave={v => save("migration_needs", v)} />
          <EditableField label="Implementation Timeline" value={lead.implementation_timeline} type="select" options={OPT.implTimeline} onSave={v => save("implementation_timeline", v)} />
          <EditableField label="EHR Name" value={lead.ehr_name} type="select" options={OPT.ehrName} onSave={v => save("ehr_name", v)} />
          <EditableField label="PMS" value={lead.pms_vendor} type="select" options={OPT.pmsVendor} onSave={v => save("pms_vendor", v)} />
          <EditableField label="Other Vendor" value={lead.other_vendor} type="select" options={OPT.otherVendor} onSave={v => save("other_vendor", v)} />
          <EditableField label="Vendor Type" value={lead.vendor_type} type="select" options={OPT.vendorType} onSave={v => save("vendor_type", v)} />
        </div>
      </Section>

      {/* Section 4 — Budget & Deal Details */}
      <Section
        id="budget"
        title="Budget & Deal Details"
        count={9}
        hasContent={Boolean(
          lead.budget_range || lead.budget || lead.upfront_budget || lead.pricing_fit ||
          lead.cost_of_lead || lead.prospect_decision_timeline || lead.closing_date_expected
        )}
      >
        <div style={grid2}>
          <EditableField label="Budget Range" value={lead.budget_range || lead.budget} type="select" options={OPT.budgetRange} onSave={v => save("budget_range", v)} highlight={hl("budget_range")} />
          <EditableField label="Upfront Budget Validation" value={lead.upfront_budget} type="select" options={OPT.upfrontBudget} onSave={v => save("upfront_budget", v)} />
          <EditableField label="Pricing Fit Status" value={lead.pricing_fit} type="badge" options={OPT.pricingFit} onSave={v => save("pricing_fit", v)} />
          <EditableField label="Cost of Lead in $" value={lead.cost_of_lead} type="number" placeholder="0" onSave={v => save("cost_of_lead", v)} />
          <EditableField label="Prospect Decision Timeline" value={lead.prospect_decision_timeline} type="select" options={OPT.prospectTimeline} onSave={v => save("prospect_decision_timeline", v)} aiFilled={!!lead.prospect_decision_timeline_ai} highlight={hl("prospect_decision_timeline")} />
          <EditableField label="Closing Date Expected" value={lead.closing_date_expected} type="date" onSave={v => save("closing_date_expected", v)} />
          <EditableField label="Closing Date" value={lead.closing_date} type="date" readOnly badge="auto" />
          <EditableField label="Quote Sent Date" value={lead.quote_sent_date} type="date" readOnly badge="auto" />
          <EditableField label="Contract Sent Date" value={lead.contract_sent_date} type="date" readOnly badge="auto" />
        </div>
      </Section>

      {/* Section 5 — Engagement & Sentiment Intelligence */}
      <Section
        id="sentiment"
        title="Engagement & Sentiment Intelligence"
        count={10}
        hasContent={Boolean(
          lead.interest_level || lead.internal_rating || lead.engagement_level ||
          lead.overall_sentiment || lead.engagement_score ||
          (Array.isArray(lead.reason_category) ? lead.reason_category.length : lead.reason_category) ||
          (Array.isArray(lead.pain_point_category) ? lead.pain_point_category.length : (lead.pain_point_category || lead.pain_points))
        )}
      >
        <div style={grid2}>
          <EditableField label="Lead Interest Level" value={lead.interest_level} type="badge" options={OPT.interestLevel} aiFilled={lead.interest_level_ai} onSave={v => save("interest_level", v)} />
          <EditableField label="Internal Lead Rating (Rep's internal rating)" value={lead.internal_rating} type="badge" options={OPT.internalRating} onSave={v => save("internal_rating", v)} />
          <EditableField label="Engagement Level" value={lead.engagement_level} type="badge" options={OPT.engagementLevel} aiFilled={lead.engagement_level_ai} onSave={v => save("engagement_level", v)} />
          <EditableField label="Overall Sentiment" value={lead.overall_sentiment} type="badge" options={OPT.sentiment} aiFilled={lead.overall_sentiment_ai} onSave={v => save("overall_sentiment", v)} />
          <div>
            <div style={{ fontSize: 10, textTransform: "uppercase", color: "#888", fontWeight: 500, marginBottom: 4, padding: "0 10px" }}>Engagement Score</div>
            <div style={{ padding: "6px 10px" }}>
              {lead.engagement_score != null && lead.engagement_score !== "" ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{lead.engagement_score}/100</div>
                  <div style={{ height: 4, background: "#eee", borderRadius: 2, marginTop: 4 }}>
                    <div style={{ width: `${Math.min(100, Number(lead.engagement_score) || 0)}%`, height: "100%", background: "#2563eb", borderRadius: 2 }} />
                  </div>
                </>
              ) : <span style={{ fontSize: 12, color: "#bbb" }}>—</span>}
            </div>
          </div>
          <EditableField label="Reason Category" value={lead.reason_category} type="multiselect" options={OPT.reasonCategory} pillColor="amber" aiFilled={lead.reason_category_ai} onSave={v => save("reason_category", v)} />
          <EditableField label="Pain Point Category" value={lead.pain_point_category || lead.pain_points} type="multiselect" options={OPT.painCategory} pillColor="red" aiFilled={lead.pain_point_category_ai} onSave={v => save("pain_point_category", v)} />
          <EditableField label="Question Insight Group" value={lead.question_insight_group} type="multiselect" options={OPT.questionInsight} pillColor="blue" onSave={v => save("question_insight_group", v)} />
          <EditableField label="Marketing Segments" value={lead.marketing_segments} type="multiselect" options={OPT.marketingSeg} pillColor="purple" onSave={v => save("marketing_segments", v)} />
        </div>
      </Section>

      {/* Section 6 — Demo & Appointment Tracking */}
      <Section
        id="demo"
        title="Demo & Appointment Tracking"
        count={17}
        hasContent={Boolean(
          lead.appointment_datetime || lead.demo_1_date || lead.demo_2_date || lead.demo_3_date ||
          lead.demo_completion_date || lead.demo_completed_by || lead.scheduled_by ||
          lead.followups_scheduled || lead.num_reschedules
        )}
      >
        <div style={grid2}>
          <EditableField label="Appointment Date-Time" value={lead.appointment_datetime} type="datetime" onSave={v => save("appointment_datetime", v)} />
          <EditableField label="Demo 1 Date" value={lead.demo_1_date} type="date" readOnly badge="auto" />
          <EditableField label="Demo 2 Date" value={lead.demo_2_date} type="date" readOnly badge="auto" />
          <EditableField label="Demo 3 Date" value={lead.demo_3_date} type="date" readOnly badge="auto" />
          <EditableField label="Demo Rescheduled" value={lead.demo_rescheduled} type="select" options={OPT.demoReschedule} readOnly badge="auto" />
          <EditableField label="Demo Completion Date" value={lead.demo_completion_date} type="date" readOnly badge="auto" />
          <EditableField label="Demo Booked By" value={lead.demo_booked_by} type="text" readOnly badge="auto" />
          <UserField label="Application Demo Completed By" value={lead.demo_completed_by} onSave={v => save("demo_completed_by", v)} testid="demo-completed-by" />
          <UserField label="Senior Resource Supervising" value={lead.senior_resource} onSave={v => save("senior_resource", v)} testid="senior-resource" />
          <UserField label="2nd Application Demo Done By" value={lead.demo2_completed_by} onSave={v => save("demo2_completed_by", v)} testid="demo2-completed-by" />
          <UserField label="2nd Demo Senior Resource Supervising" value={lead.demo2_senior} onSave={v => save("demo2_senior", v)} testid="demo2-senior" />
          <UserField label="3rd Application Demo Done By" value={lead.demo3_completed_by} onSave={v => save("demo3_completed_by", v)} testid="demo3-completed-by" />
          <UserField label="3rd Demo Senior Resource Supervising" value={lead.demo3_senior} onSave={v => save("demo3_senior", v)} testid="demo3-senior" />
          <UserField label="Scheduled By" value={lead.scheduled_by} onSave={v => save("scheduled_by", v)} testid="scheduled-by" />
          <UserField label="Follow Up Done By" value={lead.followup_done_by} onSave={v => save("followup_done_by", v)} testid="followup-done-by" />
          <EditableField label="Number of Follow-Ups Scheduled" value={lead.followups_scheduled} type="select" options={OPT.followUpCount} onSave={v => save("followups_scheduled", v)} />
          <EditableField label="Number of Follow-Ups Attended" value={lead.followups_attended} type="number" readOnly badge="auto" />
          <div>
            <div style={{ fontSize: 10, textTransform: "uppercase", color: "#888", fontWeight: 500, marginBottom: 4, padding: "0 10px" }}>Number of Reschedules <span style={{ background: "#E6F1FB", color: "#185FA5", padding: "1px 6px", borderRadius: 10, fontSize: 9, fontWeight: 600 }}>auto</span></div>
            <div style={{ padding: "4px 10px", fontSize: 13, fontWeight: 500, color: (lead.num_reschedules || 0) >= 3 ? "#A32D2D" : "#222" }}>
              {lead.num_reschedules != null ? lead.num_reschedules : <span style={{ color: "#bbb" }}>0</span>}
            </div>
          </div>
        </div>
      </Section>

      {/* Section 7 — Next Actions & Ownership */}
      <Section
        id="next"
        title="Next Actions & Ownership"
        count={6}
        hasContent={Boolean(
          lead.next_action_post_demo ||
          lead.next_action_owner ||
          lead.next_action_due_date ||
          lead.meeting_attachment ||
          (typeof lead.lead_notes === "string" && lead.lead_notes.trim()) ||
          lead.additional_info
        )}
      >
        <div style={grid2}>
          <EditableField label="Next Action (post demo)" value={lead.next_action_post_demo} type="select" options={OPT.nextActionPostDemo} onSave={v => save("next_action_post_demo", v)} />
          <UserField label="Next Action Owner" value={lead.next_action_owner} onSave={v => save("next_action_owner", v)} testid="next-action-owner" />
          <EditableField label="Due Date (for next action post demo)" value={lead.next_action_due_date} type="date" onSave={v => save("next_action_due_date", v)} />
          <div style={{ gridColumn: "1 / -1" }}>
            <EditableField label="Meeting Attachment / Reference / Notes / Link" value={lead.meeting_attachment} type="textarea" placeholder="Paste recording URL, RingCentral link, shared doc…" onSave={v => save("meeting_attachment", v)} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <NotesHistoryField
              label="Lead Notes"
              value={typeof lead.lead_notes === "string" ? lead.lead_notes : ""}
              onSave={v => save("lead_notes", v)}
              authorName={resolvedAuthorName}
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <EditableField
              label="Additional info (from CSV import)"
              value={lead.additional_info}
              type="textarea"
              placeholder="Custom CSV columns routed here appear as 'Column name: value' lines."
              onSave={v => save("additional_info", v)}
            />
          </div>
        </div>
      </Section>

      {/* Section 8 — System & Tracking (collapsed, read-only) */}
      <Section id="system" title="System & Tracking (Existing Leads Only)" count={13}>
        <div style={{ ...grid2, opacity: isSuper ? 1 : 0.92 }}>
          <EditableField label="Opportunity ID" value={lead.opportunity_id || lead.id} type="text" readOnly />
          <EditableField label="Contact ID" value={lead.contact_id} type="text" readOnly />
          <EditableField label="Pipeline Stage ID" value={lead.pipeline_stage_id} type="text" readOnly />
          <EditableField label="Pipeline ID" value={lead.pipeline_id} type="text" readOnly />
          <EditableField label="Created On" value={lead.created_at ? new Date(lead.created_at).toISOString().slice(0, 10) : ""} type="text" readOnly />
          <EditableField label="Updated On" value={lead.updated_at ? new Date(lead.updated_at).toISOString().slice(0, 10) : ""} type="text" readOnly />
          <EditableField label="Current Status - Date of Engagement" value={lead.status_date_of_engagement} type="text" readOnly />
          <DaysSinceTile label="Days Since Last Stage Change" value={lead.days_since_stage_change} />
          <DaysSinceTile label="Days Since Last Status Change" value={lead.days_since_status_change} />
          <DaysSinceTile label="Days Since Last Updated" value={lead.days_since_updated} />
          <EditableField label="Initial Stage Communication Count" value={lead.initial_stage_count} type="select" options={OPT.commCount} readOnly={!isSuper} onSave={v => save("initial_stage_count", v)} />
          <EditableField label="Stage 1 Communication Count" value={lead.stage1_count} type="select" options={OPT.commCount} readOnly={!isSuper} onSave={v => save("stage1_count", v)} />
          <EditableField label="Stage 2 Communication Count" value={lead.stage2_count} type="select" options={OPT.commCount} readOnly={!isSuper} onSave={v => save("stage2_count", v)} />
          <EditableField label="Final Stage Communication Count" value={lead.final_stage_count} type="select" options={OPT.commCount} readOnly={!isSuper} onSave={v => save("final_stage_count", v)} />
          {isLost && (<>
            <EditableField label="Lost Reason ID" value={lead.lost_reason_id} type="text" readOnly />
            <EditableField label="Lost Reason Name" value={lead.lost_reason_name} type="text" onSave={v => save("lost_reason_name", v)} />
          </>)}
        </div>
      </Section>

      {/* Section 9 — External References (collapsed). IDs sourced from the
          customer's source-of-truth CRM (HubSpot / Salesforce / etc.) via
          CSV import. Read-only — these are foreign-system identifiers and
          should never be edited by hand from the CRM UI. data-testids
          are auto-generated by EditableField as `field-contact-id`,
          `field-opportunity-id`, `field-pipeline-id`,
          `field-pipeline-stage-id`. */}
      <Section id="external-refs" title="External References" count={4}>
        <div style={grid2} data-testid="lead-external-refs">
          <EditableField label="Contact ID" value={lead.contact_id} type="text" readOnly />
          <EditableField label="Opportunity ID" value={lead.opportunity_id} type="text" readOnly />
          <EditableField label="Pipeline ID" value={lead.pipeline_id} type="text" readOnly />
          <EditableField label="Pipeline Stage ID" value={lead.pipeline_stage_id} type="text" readOnly />
        </div>
      </Section>
    </div>
    </StartCollapsedCtx.Provider>
  );
}

function DaysSinceTile({ label, value }) {
  const n = Number(value);
  const col = Number.isNaN(n) ? "#bbb" : n < 7 ? "#0F6E56" : n < 14 ? "#854F0B" : "#A32D2D";
  return (
    <div style={{ padding: "8px 10px" }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", color: "#888", fontWeight: 500 }}>{label}</div>
      <div style={{ marginTop: 3, fontSize: 13, fontWeight: 500, color: col }}>
        {Number.isNaN(n) || value == null || value === "" ? "—" : `${n} days`}
      </div>
    </div>
  );
}

// Export helpers for CSV import/export parity (serialise ; separated)
export function multiToString(arr) { return Array.isArray(arr) ? arr.filter(Boolean).join("; ") : (arr || ""); }
export function stringToMulti(str) { return String(str || "").split(";").map(s => s.trim()).filter(Boolean); }
export { OPT };
